import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { FiCalendar, FiDownload, FiPrinter, FiUsers } from "react-icons/fi";
import { fetchData } from "../../services/fetch";
import { FilterCombobox } from "../../components/Input";
import Loader from "../../components/Feedback/Loader";
import { MainContent, PageHeaderStructure } from "../../components/Layout";
import { useSSO } from "../../context/SSO";
import CompanyMultiSelect from "../../components/Catalog/CompanyMultiSelect";
import YearMultiSelect from "../../components/Catalog/YearMultiSelect";
import GriPortfolioDashboard from "../../components/Dashboard/GriPortfolioDashboard";
import GriCompanyDashboard from "../../components/Dashboard/GriCompanyDashboard";
import GriCompareDashboard from "../../components/Dashboard/GriCompareDashboard";

// GRI-05 — Dashboard GRI do painel admin. MASTER vê os 3 modos (todas as
// empresas / uma empresa / comparar); RESPONSAVEL fica travado na própria
// empresa (via company/admin_company/:userId, mesmo endpoint que a tela de
// Revisão já usa pra resolver a empresa dele).

function downloadCsv(entries, isCompareMode) {
  const header = isCompareMode
    ? ["Empresa", "Codigo", "Indicador", "Serie", "Unidade", "Ano", "Valor", "Origem"]
    : ["Codigo", "Indicador", "Serie", "Unidade", "Ano", "Valor", "Origem"];
  const lines = [header.join(";")];
  (entries || []).forEach((e) => {
    const cols = isCompareMode
      ? [e.companyName, e.code, e.name, e.series, e.unit, e.year, e.value, e.source]
      : [e.code, e.name, e.series, e.unit, e.year, e.value, e.source];
    lines.push(cols.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";"));
  });
  const csv = "﻿" + lines.join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "dashboard-gri.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function GriDashboard() {
  const { user } = useSSO();
  const roles = user?.user?.roles || [];
  const isMaster = roles.some((role) => role.name === "MASTER");
  const isResponsavel = !isMaster && roles.some((role) => role.name === "RESPONSAVEL");

  const [companyId, setCompanyId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [surveyId, setSurveyId] = useState("");
  const [selectedYears, setSelectedYears] = useState([]);
  const [compareMode, setCompareMode] = useState(false);
  const [compareCompanyIds, setCompareCompanyIds] = useState([]);
  const {
    isOpen: empresasAberto,
    onOpen: onOpenEmpresas,
    onClose: onCloseEmpresas,
  } = useDisclosure();
  const {
    isOpen: anosAberto,
    onOpen: onOpenAnos,
    onClose: onCloseAnos,
  } = useDisclosure();
  const [comparisonGroupId, setComparisonGroupId] = useState("");

  const { data: ownCompanies, isLoading: ownCompanyLoading } = useSWR(
    isResponsavel && user?.user?.id_user ? `company/admin_company/${user.user.id_user}` : null,
    fetchData
  );
  const lockedCompanyId = isResponsavel ? ownCompanies?.[0]?.id_company : null;

  const { data: companies } = useSWR(isMaster ? "company" : null, fetchData);
  const { data: templates } = useSWR("survey-template/all", fetchData);
  // GRI-06 — grupos de comparação já prontos: carregar um preenche o
  // multiseleto de empresas de uma vez, em vez de escolher uma por uma.
  const { data: comparisonGroups } = useSWR(
    isMaster && compareMode ? "comparison-group" : null,
    fetchData
  );

  const companyOptions = useMemo(
    () => (companies || []).map((c) => ({ value: c.id_company, label: c.name })),
    [companies]
  );
  // GRI-08: os questionários existentes, para o filtro de recorte por
  // questionário. `survey` devolve todos; o dashboard é MASTER-only nesse modo.
  const { data: surveys } = useSWR("survey", fetchData);

  const surveyOptions = useMemo(
    () =>
      (surveys || [])
        .map((s) => ({ value: s.id_survey, label: s.description }))
        .sort((a, b) => String(a.label).localeCompare(String(b.label), "pt-BR")),
    [surveys]
  );

  const templateOptions = useMemo(
    () => (templates || []).map((t) => ({ value: t.id_survey_template, label: t.name })),
    [templates]
  );
  const comparisonGroupOptions = useMemo(
    () =>
      (comparisonGroups || []).map((g) => ({
        value: g.id_comparison_group,
        label: `${g.name} (${g.total_empresas})`,
      })),
    [comparisonGroups]
  );

  // Dois filtros agora: montar na mão com `?` fixo grudaria o segundo
  // parâmetro como parte do valor do primeiro.
  const filtroQuery = useMemo(() => {
    const partes = [];
    if (templateId) partes.push(`templateId=${templateId}`);
    if (surveyId) partes.push(`surveyId=${surveyId}`);
    return partes.length ? `?${partes.join("&")}` : "";
  }, [templateId, surveyId]);

  const portfolioMode = isMaster && (!companyId || compareMode);
  const { data: portfolioData, isLoading: portfolioLoading } = useSWR(
    portfolioMode ? `metrics/gri/portfolio${filtroQuery}` : null,
    fetchData
  );

  const singleCompanyId = isMaster ? (companyId && !compareMode ? companyId : null) : lockedCompanyId;
  const { data: companyData, isLoading: companyLoading } = useSWR(
    singleCompanyId ? `metrics/gri/${singleCompanyId}${filtroQuery}` : null,
    fetchData
  );

  const activeEntries = portfolioMode ? portfolioData?.entries : companyData?.entries;
  const catalogTotals = portfolioMode ? portfolioData?.catalogTotals : companyData?.catalogTotals;

  const availableYears = useMemo(() => {
    const years = new Set((activeEntries || []).map((e) => e.year));
    return [...years].sort((a, b) => a - b);
  }, [activeEntries]);

  useEffect(() => {
    setSelectedYears(availableYears);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEntries]);

  const compareCompanies = useMemo(
    () =>
      (companies || [])
        .filter((c) => compareCompanyIds.includes(c.id_company))
        .map((c) => ({ id_company: c.id_company, companyName: c.name })),
    [companies, compareCompanyIds]
  );

  const compareEntries = useMemo(
    () => (compareMode ? (portfolioData?.entries || []).filter((e) => compareCompanyIds.includes(e.id_company)) : []),
    [portfolioData, compareCompanyIds, compareMode]
  );

  const isLoading = isResponsavel ? ownCompanyLoading || companyLoading : portfolioMode ? portfolioLoading : companyLoading;

  // Carregar o grupo substitui a seleção manual pelos membros do grupo — a
  // partir daí o multiseleto continua editável normalmente (é um ponto de
  // partida, não um vínculo permanente ao grupo).
  function handleSelectComparisonGroup(groupId) {
    setComparisonGroupId(groupId);
    const group = (comparisonGroups || []).find((g) => String(g.id_comparison_group) === String(groupId));
    setCompareCompanyIds(group ? group.companies.map((c) => c.id_company) : []);
  }

  function handleManualCompareChange(ids) {
    setCompareCompanyIds(ids);
    setComparisonGroupId("");
  }

  const exportEntries = compareMode
    ? compareEntries.filter((e) => selectedYears.includes(e.year))
    : (activeEntries || []).filter((e) => selectedYears.includes(e.year));

  return (
    <MainContent>
      <style>{`@media print { .gri-dashboard-no-print { display: none !important; } }`}</style>

      <PageHeaderStructure
        title="Dashboard GRI"
        subtitle="Indicadores GRI reportados pelas empresas, a partir de questões classificadas nos templates."
      >
        <Flex gap="8px" className="gri-dashboard-no-print">
          <Button leftIcon={<FiDownload />} variant="ghost" onClick={() => downloadCsv(exportEntries, compareMode)}>
            Exportar CSV
          </Button>
          <Button leftIcon={<FiPrinter />} variant="ghost" onClick={() => window.print()}>
            Exportar PDF
          </Button>
        </Flex>
      </PageHeaderStructure>

      <Box className="gri-dashboard-no-print" bg="white" borderRadius="12px" p="16px" mt="24px" mb="24px">
        {/* Linha 1 — filtros principais. "Empresa" some no modo comparar (não
            faz sentido escolher 1 empresa e comparar ao mesmo tempo — os
            seletores da linha 2 assumem esse papel). "Comparar" fica no fim
            da linha (ml=auto): é um MODO (troca o que aparece embaixo e o
            que aparece na linha 1), não mais um filtro igual aos outros. */}
        <Flex gap="12px" flexWrap="wrap" align="flex-end">
          {isMaster && !compareMode && (
            <Box w="260px">
              <FilterCombobox
                label="Empresa"
                placeholder="Todas as empresas"
                searchPlaceholder="Buscar empresa"
                value={companyId}
                options={companyOptions}
                onChange={setCompanyId}
                clearLabel="Todas as empresas"
              />
            </Box>
          )}

          {/* Template e Questionário são MUTUAMENTE EXCLUSIVOS: um recorta o
              universo de indicadores, o outro recorta a medida. Combinados,
              produzem um conjunto que ninguém consegue explicar — escolher um
              desabilita o outro, e trocar limpa o anterior. */}
          <Box w="260px">
            <FilterCombobox
              label="Template"
              placeholder={surveyId ? "Desabilitado — questionário escolhido" : "Todos os templates"}
              searchPlaceholder="Buscar template"
              value={templateId}
              options={templateOptions}
              onChange={(valor) => {
                setTemplateId(valor);
                if (valor) setSurveyId("");
              }}
              clearLabel="Todos os templates"
              isDisabled={!!surveyId}
            />
          </Box>

          <Box w="260px">
            <FilterCombobox
              label="Questionário"
              placeholder={templateId ? "Desabilitado — template escolhido" : "Todos os questionários"}
              searchPlaceholder="Buscar questionário"
              value={surveyId}
              options={surveyOptions}
              onChange={(valor) => {
                setSurveyId(valor);
                if (valor) setTemplateId("");
              }}
              clearLabel="Todos os questionários"
              isDisabled={!!templateId}
            />
          </Box>

          {availableYears.length > 0 && (
            <Box w="180px">
              <Text fontSize="xs" color="#818A8F" mb="8px">
                Anos
              </Text>
              {/* Mesmo tratamento das empresas: a lista e as pílulas moram no
                  drawer, e aqui fica só a contagem. */}
              <Button
                onClick={onOpenAnos}
                variant="outline"
                w="100%"
                h="40px"
                justifyContent="space-between"
                rightIcon={<FiCalendar />}
                fontWeight={500}
                fontSize="sm"
              >
                {selectedYears.length > 0
                  ? `${selectedYears.length} ano${selectedYears.length > 1 ? "s" : ""}`
                  : "Todos os anos"}
              </Button>
            </Box>
          )}

          {isMaster && (
            <Button
              ml="auto"
              leftIcon={<FiUsers />}
              variant={compareMode ? "primary" : "outline"}
              onClick={() => {
                const next = !compareMode;
                setCompareMode(next);
                if (next) setCompanyId("");
              }}
            >
              Comparar empresas
            </Button>
          )}
        </Flex>

        {/* Linha 2 — só existe dentro do modo comparar. Fundo levemente
            destacado + divisor: sinaliza que esses dois campos são
            específicos desse modo, não filtros gerais do dashboard. */}
        {isMaster && compareMode && (
          <Flex
            gap="12px"
            flexWrap="wrap"
            align="flex-end"
            mt="16px"
            pt="16px"
            borderTop="1px solid #EDF2F7"
          >
            {comparisonGroupOptions.length > 0 && (
              <Box w="240px">
                <FilterCombobox
                  label="Grupo de comparação"
                  placeholder="Escolher empresas uma a uma"
                  searchPlaceholder="Buscar grupo"
                  value={comparisonGroupId}
                  options={comparisonGroupOptions}
                  onChange={handleSelectComparisonGroup}
                  clearLabel="Escolher empresas uma a uma"
                />
              </Box>
            )}

            {/* As tags das empresas escolhidas saíram da barra de filtros e
                foram para um drawer. Com dezenas de empresas selecionadas a
                lista empurrava o gráfico para fora da tela — e o filtro
                passava a ocupar mais espaço que o dado que ele filtra. Aqui
                fica só a contagem; a lista mora atrás do botão. */}
            <Box minW="240px">
              <Text fontSize="xs" color="#818A8F" mb="8px">
                Empresas para comparar
              </Text>
              <Button
                onClick={onOpenEmpresas}
                variant="outline"
                w="100%"
                h="40px"
                justifyContent="space-between"
                rightIcon={<FiUsers />}
                fontWeight={500}
                fontSize="sm"
              >
                {compareCompanyIds.length > 0
                  ? `${compareCompanyIds.length} empresa${compareCompanyIds.length > 1 ? "s" : ""}`
                  : "Escolher empresas"}
              </Button>
            </Box>
          </Flex>
        )}
      </Box>

      <Drawer isOpen={anosAberto} onClose={onCloseAnos} placement="right" size="sm">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader fontSize="md">Anos</DrawerHeader>

          <DrawerBody>
            <Text fontSize="xs" color="#818A8F" mb="12px">
              Sem nenhum ano marcado, o dashboard mostra todos.
            </Text>
            <YearMultiSelect
              years={availableYears}
              value={selectedYears}
              onChange={setSelectedYears}
            />
          </DrawerBody>

          <DrawerFooter borderTop="1px solid #EDF2F7" justifyContent="space-between">
            <Text fontSize="sm" color="#5C6066">
              {selectedYears.length} selecionado
              {selectedYears.length === 1 ? "" : "s"}
            </Text>
            <Flex gap="8px">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedYears([])}
                isDisabled={selectedYears.length === 0}
              >
                Limpar
              </Button>
              <Button size="sm" colorScheme="blue" onClick={onCloseAnos}>
                Concluir
              </Button>
            </Flex>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* A escolha das empresas mora aqui: com 300 empresas no sistema, a
          lista precisa de espaço e de busca, e nada disso cabe numa barra de
          filtros sem empurrar o gráfico para fora da tela. */}
      <Drawer isOpen={empresasAberto} onClose={onCloseEmpresas} placement="right" size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader fontSize="md">Empresas para comparar</DrawerHeader>

          <DrawerBody>
            <Text fontSize="xs" color="#818A8F" mb="12px">
              Escolha as empresas na lista. As selecionadas aparecem abaixo e
              podem ser removidas uma a uma.
            </Text>
            <CompanyMultiSelect
              companies={companies || []}
              value={compareCompanyIds}
              onChange={handleManualCompareChange}
            />
          </DrawerBody>

          <DrawerFooter borderTop="1px solid #EDF2F7" justifyContent="space-between">
            <Text fontSize="sm" color="#5C6066">
              {compareCompanyIds.length} selecionada
              {compareCompanyIds.length === 1 ? "" : "s"}
            </Text>
            <Flex gap="8px">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleManualCompareChange([])}
                isDisabled={compareCompanyIds.length === 0}
              >
                Limpar
              </Button>
              <Button size="sm" colorScheme="blue" onClick={onCloseEmpresas}>
                Concluir
              </Button>
            </Flex>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {isLoading ? (
        <Loader />
      ) : compareMode && !templateId && !surveyId ? (
        /* O comparativo EXIGE um recorte. Sem template nem questionário, ele
           cruzaria medidas de ciclos e escopos diferentes na mesma barra — o
           gráfico sai, mas não quer dizer nada. Melhor pedir a escolha do que
           entregar um número que ninguém consegue interpretar. */
        <Flex
          bg="#FFF"
          borderRadius="12px"
          p={["32px", "48px"]}
          direction="column"
          align="center"
          textAlign="center"
          gap="8px"
        >
          <Text fontWeight={600} color="#16262E">
            Escolha um template ou um questionário
          </Text>
          <Text color="#818A8F" fontSize="14px" maxW="460px">
            A comparação entre empresas precisa de um recorte comum. Sem ele, os
            valores viriam de ciclos e escopos diferentes na mesma barra.
          </Text>
        </Flex>
      ) : compareMode ? (
        <GriCompareDashboard entries={compareEntries} companies={compareCompanies} selectedYears={selectedYears} />
      ) : portfolioMode ? (
        <GriPortfolioDashboard entries={portfolioData?.entries} catalogTotals={catalogTotals} selectedYears={selectedYears} />
      ) : (
        <GriCompanyDashboard entries={companyData?.entries} catalogTotals={catalogTotals} selectedYears={selectedYears} />
      )}
    </MainContent>
  );
}
